import { motion } from "framer-motion";
import { ArrowRight, FileText, ShieldCheck, Zap } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import StatementReviewUpload from "@/components/StatementReviewUpload";
import WhatsAppButton from "@/components/WhatsAppButton";
import TrustBar from "@/components/TrustBar";
import RelatedTools from "@/components/RelatedTools";
import SolutionsCrossSell from "@/components/SolutionsCrossSell";
import { fadeUp, stagger, viewportOnce } from "@/utils/motionVariants";

export default function StatementReviewPage() {
  return (
    <div>
      <SEOHead title="Statement Review Upload" description="Upload your card processing statement and get a tailored review. We'll identify where you could save. Free, secure, no obligation." path="/statement-review" />

      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Statement Review</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">Upload your statement. <span className="gradient-text">We&apos;ll do the rest.</span></motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">Send us a recent card processing statement and our team will review it to show you exactly where you stand — and where you could save.</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
              <a href="#upload" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm inline-flex items-center gap-2 transition-colors btn-shine">Upload Now <ArrowRight size={16} /></a>
              <WhatsAppButton variant="secondary" placement="statement" label="Or WhatsApp us a photo" />
            </motion.div>
            <motion.p variants={fadeUp} className="text-sm text-brand-grey mt-3"><em>Rather not upload? Just WhatsApp us a photo of your statement.</em></motion.p>
          </motion.div>
        </div>
      </section>

      <TrustBar />

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: FileText, title: "Upload securely", desc: "Your files are handled securely and only used for the purpose of your review. We accept PDF, JPG, PNG, and WEBP." },
              { icon: ShieldCheck, title: "Expert review", desc: "Our team will review your statement line by line and identify any areas where your pricing may be improved." },
              { icon: Zap, title: "Tailored response", desc: "You'll receive a personalised comparison showing your current costs versus what we could offer." },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center flex-shrink-0"><item.icon size={20} className="text-brand-aqua" /></div>
                <div><h3 className="font-heading font-bold text-brand-black mb-1">{item.title}</h3><p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="upload" className="py-20 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <StatementReviewUpload />
        </div>
      </section>
      <RelatedTools currentPath="/statement-review" />
      <SolutionsCrossSell currentPath="/statement-review" />
    </div>
  );
}
